import { prepareImageForEditing } from '~core/image';
import { getState } from '~src/state';
import { prepareCanvasVisualExportSelection } from '~core/actions/prepareCanvasVisualExportSelection';
import {
  assertValidCanvasDataUrl,
  uploadDataUrlToUploader,
} from './backgroundFusionCanvas';

const getObjectExportDimensions = (object) => {
  const width =
    (typeof object?.getScaledWidth === 'function'
      ? object.getScaledWidth()
      : object?.width * (object?.scaleX || 1)) || object?.width;
  const height =
    (typeof object?.getScaledHeight === 'function'
      ? object.getScaledHeight()
      : object?.height * (object?.scaleY || 1)) || object?.height;

  return {
    width: Math.round(width || 0),
    height: Math.round(height || 0),
  };
};

export const getBackgroundFusionSubject = (selection) =>
  selection?._magicFrame?.type === 'MagicFrame'
    ? selection._magicFrame
    : selection;

export const isMagicFrameSubject = (subject) => subject?.type === 'MagicFrame';

export const canUseBackgroundFusionSubject = (selection) => {
  const subject = getBackgroundFusionSubject(selection);

  return Boolean(
    subject &&
      (isMagicFrameSubject(subject) || typeof subject.getSrc === 'function')
  );
};

export const exportMagicFrameSubjectDataUrl = (magicFrame) => {
  if (!magicFrame) {
    throw new Error('Unable to export Magic Frame for background fusion');
  }

  if (typeof magicFrame.setCoords === 'function') {
    magicFrame.setCoords();
  }

  const dimensions = getObjectExportDimensions(magicFrame);
  const canvas =
    magicFrame.canvas ||
    (typeof window !== 'undefined' ? window.canvas : undefined);
  const { selection } = prepareCanvasVisualExportSelection({
    canvas,
    activeObject: magicFrame,
  });

  if (!selection || typeof selection.toDataURL !== 'function') {
    throw new Error('Unable to export Magic Frame for background fusion');
  }

  const dataUrl = selection.toDataURL();

  return assertValidCanvasDataUrl(dataUrl, dimensions);
};

export const prepareBackgroundFusionSubjectUrl = async (selection) => {
  const subject = getBackgroundFusionSubject(selection);

  if (isMagicFrameSubject(subject)) {
    return uploadDataUrlToUploader({
      dataUrl: exportMagicFrameSubjectDataUrl(subject),
      uploader: getState('uploader.ephemeral'),
      name: `background-fusion-subject-${Date.now()}.png`,
    });
  }

  return prepareImageForEditing(subject, false);
};

export const removeBackgroundFusionSubjectFromCanvas = (
  selection,
  canvas = typeof window !== 'undefined' ? window.canvas : undefined
) => {
  const subject = getBackgroundFusionSubject(selection);

  if (!subject || !canvas) {
    return;
  }

  if (isMagicFrameSubject(subject) && subject.editingImg) {
    canvas.remove(subject.editingImg);
  }

  canvas.remove(subject);
};

export const removeMagicFrameKeepingSubjectImage = (
  magicFrame,
  canvas = typeof window !== 'undefined' ? window.canvas : undefined
) => {
  const subject = getBackgroundFusionSubject(magicFrame);

  if (!isMagicFrameSubject(subject) || !subject.editingImg || !canvas) {
    return null;
  }

  const image = subject.editingImg;

  if (typeof subject.updateLinked === 'function') {
    subject.updateLinked();
  }

  const canvasObjects =
    typeof canvas.getObjects === 'function' ? canvas.getObjects() : [];
  if (!image.canvas || !canvasObjects.includes(image)) {
    canvas.add(image);
  }

  image.set({
    opacity: 1,
    evented: true,
    selectable: true,
    excludeFromExport: false,
    erasable: true,
  });
  delete image._magicFrame;
  delete image.relationship;

  if (typeof image.setCoords === 'function') {
    image.setCoords();
  }

  subject.editingImg = null;
  canvas.remove(subject);

  if (typeof canvas.setActiveObject === 'function') {
    canvas.setActiveObject(image);
  }

  if (typeof canvas.renderAll === 'function') {
    canvas.renderAll();
  }

  return image;
};
